import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { Link, useParams } from 'react-router-dom';
import ProformaAiReviewButton from '@v2/features/ai/ProformaAiReviewButton';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    PiArrowSquareOutBold,
    PiCaretDownBold,
    PiCheckBold,
    PiCheckCircleBold,
    PiDotsThreeBold,
    PiFunnelBold,
    PiCubeBold,
    PiFileTextBold,
    PiFileXlsBold,
    PiListBulletsBold,
    PiPencilSimpleBold,
    PiSquaresFourBold,
    PiTrashBold,
    PiWarningCircleBold,
    PiXBold,
} from 'react-icons/pi';
import ConfirmModal from '@v2/components/ui/ConfirmModal';
import V2Modal from '@v2/components/ui/V2Modal';
import FileDropzone from '@v2/components/ui/FileDropzone';
import ClientAvatarStack, { type ClientThumb } from '@v2/features/pi-check/ClientAvatarStack';
import OrderRefAvatarStack, { type OrderRef } from '@v2/features/pi-check/OrderRefAvatarStack';
import PiCheckColorModal, {
    type ColorSlotDetail,
    type ColorSlotKey,
} from '@v2/features/pi-check/PiCheckColorModal';
import PiCheckSkuModal, { type PiCheckSkuRow } from '@v2/features/pi-check/PiCheckSkuModal';
import SkuReconciliationPanel, { type SkuGroup, type SkuSummary } from '@v2/features/pi-check/SkuReconciliationPanel';
import { LoadingState } from '@v2/design-system';
import api from '@v2/lib/api';
import { ColorHexMapsContext, useColorHexMaps, type ColorHexMaps } from '@v2/features/colors/colorHex';
import { COLOR_SLOTS, COLOR_SLOT_LABELS, ColorDot, ColorLegend, isNoCustomization } from '@v2/features/colors/ColorDot';
import { ContainerSlot } from '@v2/features/containers/ContainerSlot';
import { fadeZoomIn, pop, useCountUp, useFillBar, useStaggerIn } from '@v2/lib/motion';

type State = {
    shipment: { id: number; order_number: string | null };
    po: {
        id: number;
        code: string;
        status: 'draft' | 'closed';
        is_closed: boolean;
        total_usd: number;
        total_cbm: number;
        containers_count: number;
        items_count: number;
        supplier_label: string | null;
    } | null;
    pi: {
        id: number;
        pi_number: string | null;
        pi_date: string | null;
        supplier_name: string | null;
        total_usd: number;
        total_cbm: number;
        containers_count: number;
        items_count: number;
        original_filename: string;
        file_url: string | null;
        parsed_at: string | null;
        uploaded_by_name: string | null;
    } | null;
    comparison: {
        rows: ComparisonRow[];
        summary: ComparisonSummary;
        orders?: OrderGroup[];
        sku_groups?: SkuGroup[];
    } | null;
    cbm_per_container: number;
    color_hex_maps?: ColorHexMaps;
};

type Side = {
    code: string;
    source_code?: string;
    internal_code?: string | null;
    supplier_code?: string | null;
    name: string | null;
    qty: number;
    unit_price: number;
    total: number;
    cbm_unit: number;
    total_cbm: number;
    client_id?: number | null;
    client_name?: string | null;
    client_logo_url?: string | null;
    frame?: string | null;
    upholstery?: string | null;
    stitches?: string | null;
    arm?: string | null;
    image_url?: string | null;
    order_number?: string | null;
    line_count?: number;
};

type ComparisonSummary = {
    matched: number;
    qty_mismatch: number;
    color_mismatch?: number;
    price_mismatch: number;
    only_in_pi: number;
    only_in_po: number;
    total_po: number;
    total_pi: number;
    total_po_qty?: number;
    total_pi_qty?: number;
    total_rows?: number;
    sku?: SkuSummary;
};

type OrderGroup = {
    order_number: string;
    rows: ComparisonRow[];
    summary: ComparisonSummary;
};

type ComparisonRow = PiCheckSkuRow & {
    order_number?: string | null;
    status: 'ok' | 'mismatch' | 'only_in_pi' | 'only_in_po';
    issues: string[];
    color_diff?: string[];
    color_slots?: Partial<Record<ColorSlotKey, ColorSlotDetail>>;
    clients?: ClientThumb[];
    order_refs?: OrderRef[];
    po: Side | null;
    pi: Side | null;
    qty_diff: number | null;
    price_diff: number | null;
    total_diff: number | null;
};

type ViewMode = 'sku' | 'order';

const fmtUSD = (n: number) =>
    '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtQty = (n: number) =>
    n.toLocaleString('pt-BR', { maximumFractionDigits: 2 });
const fmtContainers = (n: number) =>
    `${n} ${n === 1 ? 'container' : 'containers'}`;
const fmtDate = (s: string | null) => {
    if (!s) return '—';
    try {
        return new Date(s).toLocaleDateString('pt-BR');
    } catch {
        return s;
    }
};

const SECONDARY_BTN =
    'inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60';

/** Linhas animadas na cascata: além disso o atraso acumulado fica perceptível. */
const MOTION_ROWS = 25;

function matchesSearch(r: ComparisonRow, q: string): boolean {
    if (!q) return true;
    return [
        r.product_code,
        r.internal_code,
        r.supplier_code,
        r.product_name,
        r.po?.name,
        r.pi?.name,
        r.order_number,
        ...(r.po_source_codes ?? []),
        ...(r.pi_source_codes ?? []),
    ].some((v) => typeof v === 'string' && v.toLowerCase().includes(q));
}

/** Abre a foto ampliada a partir de qualquer linha (evita passar callback por 4 níveis). */
const ZoomRowContext = createContext<(r: ComparisonRow) => void>(() => {});

type RowFilter = 'all' | 'ok' | 'qty_mismatch' | 'color_mismatch' | 'only_in_pi' | 'only_in_po';

function matchesRowFilter(r: ComparisonRow, filter: RowFilter): boolean {
    switch (filter) {
        case 'ok':
            return r.status === 'ok';
        case 'qty_mismatch':
            return r.issues.includes('quantity');
        case 'color_mismatch':
            return r.issues.includes('color');
        case 'only_in_pi':
            return r.status === 'only_in_pi';
        case 'only_in_po':
            return r.status === 'only_in_po';
        default:
            return true;
    }
}

function emptyFilterMessage(filter: RowFilter): string {
    switch (filter) {
        case 'ok':
            return 'Nenhum item com status OK.';
        case 'qty_mismatch':
            return 'Nenhuma divergência de quantidade.';
        case 'color_mismatch':
            return 'Nenhuma divergência de cor.';
        case 'only_in_pi':
            return 'Nenhum item só na PI.';
        case 'only_in_po':
            return 'Nenhum item só na PO.';
        default:
            return 'Nenhum item para comparar.';
    }
}

export default function ShipmentProformaInvoicePage() {
    const { id } = useParams<{ id: string }>();
    const qc = useQueryClient();
    const queryKey = ['shipment-pi', id];
    const [toast, setToast] = useState<{ kind: 'success' | 'error'; msg: string } | null>(null);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [filter, setFilter] = useState<RowFilter>('all');
    const [viewMode, setViewMode] = useState<ViewMode>('sku');
    const [collapsedOrders, setCollapsedOrders] = useState<Set<string>>(new Set());
    const [skuModalRow, setSkuModalRow] = useState<ComparisonRow | null>(null);
    const [zoomRow, setZoomRow] = useState<ComparisonRow | null>(null);
    const [search, setSearch] = useState('');
    const [colorModal, setColorModal] = useState<{
        row: ComparisonRow;
        slot: ColorSlotKey;
    } | null>(null);

    const { data, isLoading, error } = useQuery({
        queryKey,
        queryFn: async () =>
            (await api.get<State>(`/shipments/${id}/proforma-invoice`)).data,
    });

    const uploadMut = useMutation({
        mutationFn: async (file: File) => {
            const fd = new FormData();
            fd.append('file', file);
            const r = await api.post(`/shipments/${id}/proforma-invoice`, fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            return r.data as State;
        },
        onSuccess: (s) => {
            qc.setQueryData(queryKey, s);
            setToast({ kind: 'success', msg: 'Proforma Invoice importada com sucesso.' });
        },
        onError: (e: any) =>
            setToast({
                kind: 'error',
                msg: e?.response?.data?.message ?? 'Falha ao importar.',
            }),
    });

    const deleteMut = useMutation({
        mutationFn: async () =>
            (await api.delete<State>(`/shipments/${id}/proforma-invoice`)).data,
        onSuccess: (s) => {
            qc.setQueryData(queryKey, s);
            setToast({ kind: 'success', msg: 'PI removida.' });
        },
    });

    if (isLoading) return <LoadingState />;
    if (error) return <p className="p-6 text-sm text-red-600">Erro ao carregar.</p>;
    if (!data) return null;

    const { shipment, po, pi, comparison, cbm_per_container } = data;
    const rows = comparison?.rows ?? [];
    const orderGroups = comparison?.orders ?? [];
    const q = search.trim().toLowerCase();
    const visible = (r: ComparisonRow) => matchesRowFilter(r, filter) && matchesSearch(r, q);
    const visibleRows = rows.filter(visible);
    const visibleOrderGroups = orderGroups
        .map((g) => ({
            ...g,
            rows: g.rows.filter(visible),
        }))
        .filter((g) => g.rows.length > 0);
    // Busca não entra: reanimar a cada tecla cansa; filtro/visão sim (feedback da troca).
    const motionKey = `${viewMode}|${filter}`;

    const toggleOrderCollapsed = (orderNumber: string) => {
        setCollapsedOrders((prev) => {
            const next = new Set(prev);
            if (next.has(orderNumber)) {
                next.delete(orderNumber);
            } else {
                next.add(orderNumber);
            }
            return next;
        });
    };
    const containerDiff =
        po && pi ? pi.containers_count - po.containers_count : null;

    return (
        <ColorHexMapsContext.Provider value={data.color_hex_maps ?? null}>
        <ZoomRowContext.Provider value={setZoomRow}>
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <nav className="text-xs text-gray-500">
                        <Link to="/shipments" className="hover:underline">Embarques</Link>
                        <span className="mx-1">&gt;</span>
                        <Link to={`/shipments/${shipment.id}`} className="hover:underline">
                            {shipment.order_number ?? `#${shipment.id}`}
                        </Link>
                        <span className="mx-1">&gt;</span>
                        <span>Validação PI × PO</span>
                    </nav>
                    <h1 className="mt-1 text-2xl font-bold text-gray-800">
                        Validação Proforma Invoice × Purchase Order
                    </h1>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    {po && pi && comparison && <ProformaAiReviewButton shipmentId={shipment.id} />}
                    {po && (
                        <Link to={`/shipments/${shipment.id}/purchase-order`} className={SECONDARY_BTN}>
                            <PiFileTextBold className="size-4 text-gray-500" /> Ver PO {po.code}
                        </Link>
                    )}
                    {pi?.file_url && (
                        <a href={pi.file_url} target="_blank" rel="noreferrer" className={SECONDARY_BTN}>
                            <PiArrowSquareOutBold className="size-4 text-blue-600" /> Baixar PI
                        </a>
                    )}
                    {pi && (
                        // Destrutivo fora da linha principal (mesmo padrão da tela da PO).
                        <Menu as="div" className="relative">
                            <MenuButton className={SECONDARY_BTN + ' px-2.5'} title="Mais ações" aria-label="Mais ações">
                                <PiDotsThreeBold className="size-5" />
                            </MenuButton>
                            <MenuItems
                                anchor="bottom end"
                                className="z-[60] mt-1 w-56 origin-top-right rounded-xl border border-gray-200 bg-white p-1 shadow-lg focus:outline-none"
                            >
                                <MenuItem>
                                    <button
                                        type="button"
                                        onClick={() => setConfirmDelete(true)}
                                        disabled={deleteMut.isPending}
                                        className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-red-600 data-[focus]:bg-red-50 disabled:opacity-60"
                                    >
                                        <PiTrashBold className="size-4" /> Remover PI importada
                                    </button>
                                </MenuItem>
                            </MenuItems>
                        </Menu>
                    )}
                </div>
            </div>

            {toast && (
                <div
                    className={`rounded-lg border px-4 py-2 text-sm ${
                        toast.kind === 'success'
                            ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                            : 'border-red-200 bg-red-50 text-red-700'
                    }`}
                >
                    {toast.msg}
                </div>
            )}

            {!po && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                    Este embarque não tem Purchase Order. Crie a PO antes de importar a PI.
                </div>
            )}

            {/* Cards superiores: PO + PI */}
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <SummaryCard
                    title="Purchase Order"
                    subtitle={po ? po.code : 'Sem PO'}
                    items={[
                        { label: 'Itens', value: po ? <AnimatedNumber value={po.items_count} format={(n) => String(Math.round(n))} /> : '—' },
                        { label: 'Total USD', value: po ? <AnimatedNumber value={po.total_usd} format={fmtUSD} /> : '—' },
                        { label: 'CBM', value: po ? <AnimatedNumber value={po.total_cbm} format={fmtQty} /> : '—' },
                        { label: 'Fornecedor', value: po?.supplier_label ?? '—' },
                    ]}
                    footer={
                        po ? (
                            <ContainersStrip
                                totalCbm={po.total_cbm}
                                containersCount={po.containers_count}
                                cbmPerContainer={cbm_per_container}
                                accent="emerald"
                            />
                        ) : undefined
                    }
                />
                <SummaryCard
                    title="Proforma Invoice"
                    subtitle={pi?.pi_number ?? 'Nenhuma PI importada'}
                    accent={pi ? 'emerald' : undefined}
                    items={[
                        { label: 'Data', value: fmtDate(pi?.pi_date ?? null) },
                        { label: 'Itens', value: pi ? <AnimatedNumber value={pi.items_count} format={(n) => String(Math.round(n))} /> : '—' },
                        { label: 'Total USD', value: pi ? <AnimatedNumber value={pi.total_usd} format={fmtUSD} /> : '—' },
                        { label: 'CBM', value: pi ? <AnimatedNumber value={pi.total_cbm} format={fmtQty} /> : '—' },
                    ]}
                    footer={
                        pi ? (
                            <ContainersStrip
                                totalCbm={pi.total_cbm}
                                containersCount={pi.containers_count}
                                cbmPerContainer={cbm_per_container}
                                accent="blue"
                                diffLabel={
                                    containerDiff === null
                                        ? undefined
                                        : containerDiff !== 0
                                          ? `Δ vs PO: ${containerDiff > 0 ? '+' : ''}${containerDiff}`
                                          : 'Igual à PO'
                                }
                            />
                        ) : undefined
                    }
                />
            </div>

            {/* Upload / Status PI */}
            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                {!pi ? (
                    <div className="flex flex-col items-center gap-3 py-6 text-center">
                        <div className="rounded-full bg-primary-lighter p-4 text-primary">
                            <PiFileXlsBold size={28} />
                        </div>
                        <h2 className="text-lg font-semibold text-gray-800">
                            Importar Proforma Invoice (Excel)
                        </h2>
                        <p className="max-w-md text-sm text-gray-600">
                            Anexe o arquivo .xlsx enviado pelo fornecedor (modelo Real Leader).
                            O sistema irá comparar automaticamente cada SKU com a PO.
                        </p>
                        <FileDropzone
                            className="w-full max-w-lg"
                            accept=".xlsx,.xls"
                            maxSizeMb={20}
                            loading={uploadMut.isPending}
                            disabled={uploadMut.isPending || !po}
                            label="Arraste a PI aqui"
                            hint="Excel .xlsx / .xls · clique para selecionar"
                            showSelected={false}
                            onFiles={(files) => {
                                if (files[0]) uploadMut.mutate(files[0]);
                            }}
                            onError={(msg) => setToast({ kind: 'error', msg })}
                        />
                    </div>
                ) : (
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                            <div className="rounded-md bg-emerald-100 p-2 text-emerald-700">
                                <PiFileXlsBold size={20} />
                            </div>
                            <div>
                                <div className="text-sm font-semibold text-gray-800">
                                    {pi.original_filename}
                                </div>
                                <div className="text-xs text-gray-500">
                                    {pi.pi_number ? `PI ${pi.pi_number} · ` : ''}
                                    {fmtDate(pi.pi_date)}
                                    {pi.uploaded_by_name ? ` · enviado por ${pi.uploaded_by_name}` : ''}
                                </div>
                            </div>
                        </div>
                        <div className="flex min-w-[16rem] flex-1 flex-col items-stretch gap-2 sm:max-w-sm sm:flex-none">
                            <FileDropzone
                                accept=".xlsx,.xls"
                                maxSizeMb={20}
                                loading={uploadMut.isPending}
                                disabled={uploadMut.isPending}
                                label="Substituir PI"
                                hint="Arraste ou clique · .xlsx / .xls"
                                showSelected={false}
                                className="[&>label]:py-4"
                                onFiles={(files) => {
                                    if (files[0]) uploadMut.mutate(files[0]);
                                }}
                                onError={(msg) => setToast({ kind: 'error', msg })}
                            />
                            <ConfirmModal
                                open={confirmDelete}
                                onClose={() => setConfirmDelete(false)}
                                onConfirm={() => {
                                    setConfirmDelete(false);
                                    deleteMut.mutate();
                                }}
                                title="Remover a PI importada?"
                                description="Esta ação não pode ser desfeita."
                                confirmLabel={deleteMut.isPending ? 'Removendo…' : 'Remover'}
                                loading={deleteMut.isPending}
                            />
                        </div>
                    </div>
                )}
            </div>

            {comparison && (
                <>
                    <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
                        Foco operacional: <strong>produto</strong> (+PI / +PO), <strong>quantidade</strong>{' '}
                        e <strong>cores</strong> (uma coluna por tipo: estrutura, estofado, costura, braço).
                        Preços ficam na PO/PI; não entram como divergência aqui.
                        SKUs: cadastre código interno e do fornecedor em{' '}
                        <Link to="/products" className="font-semibold underline">
                            Produtos
                        </Link>
                        .
                    </div>

                    <ConferenceProgress
                        matched={comparison.summary.sku?.ok ?? comparison.summary.matched}
                        total={comparison.summary.sku?.total ?? rows.length}
                    />

                    {comparison.sku_groups && comparison.summary.sku && (
                        <SkuReconciliationPanel
                            groups={comparison.sku_groups}
                            summary={comparison.summary.sku}
                            totalPoQty={comparison.summary.total_po_qty ?? 0}
                            totalPiQty={comparison.summary.total_pi_qty ?? 0}
                            onFocusSku={(code) => {
                                setViewMode('sku');
                                setFilter('all');
                                setSearch(code);
                                requestAnimationFrame(() =>
                                    document.getElementById('pi-check-details')?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
                                );
                            }}
                        />
                    )}

                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <p id="pi-check-details" className="scroll-mt-24 text-xs text-gray-500">
                            <strong className="text-gray-700">Detalhe por cor</strong> · cada linha é um código numa cor (pedidos somados em "Por cor"). Clique num card para filtrar.{' '}
                            {filter !== 'all' ? (
                                <button
                                    type="button"
                                    onClick={() => setFilter('all')}
                                    className="font-semibold text-primary hover:underline"
                                >
                                    Limpar filtro
                                </button>
                            ) : null}
                        </p>
                        <ViewModeToggle mode={viewMode} onChange={setViewMode} />
                    </div>

                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
                        <FilterChip
                            color="primary"
                            label="Todos"
                            value={rows.length}
                            active={filter === 'all'}
                            onClick={() => setFilter('all')}
                        />
                        <FilterChip
                            color="emerald"
                            icon={<PiCheckCircleBold />}
                            label="OK"
                            value={comparison.summary.matched}
                            active={filter === 'ok'}
                            onClick={() => setFilter(filter === 'ok' ? 'all' : 'ok')}
                            disabled={comparison.summary.matched === 0}
                        />
                        <FilterChip
                            color="red"
                            icon={<PiWarningCircleBold />}
                            label="Qtd divergente"
                            value={comparison.summary.qty_mismatch}
                            active={filter === 'qty_mismatch'}
                            onClick={() =>
                                setFilter(filter === 'qty_mismatch' ? 'all' : 'qty_mismatch')
                            }
                            disabled={comparison.summary.qty_mismatch === 0}
                        />
                        <FilterChip
                            color="amber"
                            icon={<PiWarningCircleBold />}
                            label="Cor divergente"
                            value={comparison.summary.color_mismatch ?? 0}
                            active={filter === 'color_mismatch'}
                            onClick={() =>
                                setFilter(filter === 'color_mismatch' ? 'all' : 'color_mismatch')
                            }
                            disabled={(comparison.summary.color_mismatch ?? 0) === 0}
                        />
                        <FilterChip
                            color="rose"
                            label="Só na PI (falta PO)"
                            value={comparison.summary.only_in_pi}
                            active={filter === 'only_in_pi'}
                            onClick={() =>
                                setFilter(filter === 'only_in_pi' ? 'all' : 'only_in_pi')
                            }
                            disabled={comparison.summary.only_in_pi === 0}
                        />
                        <FilterChip
                            color="slate"
                            label="Só na PO (falta PI)"
                            value={comparison.summary.only_in_po}
                            active={filter === 'only_in_po'}
                            onClick={() =>
                                setFilter(filter === 'only_in_po' ? 'all' : 'only_in_po')
                            }
                            disabled={comparison.summary.only_in_po === 0}
                        />
                    </div>

                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-xl border border-gray-200 bg-white px-3 py-2 shadow-sm">
                        <div className="relative min-w-[14rem] flex-1">
                            <PiFunnelBold className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Filtrar por SKU, código do fornecedor, nome ou pedido…"
                                className="w-full rounded-lg border border-gray-200 bg-gray-50 py-1.5 pl-8 pr-8 text-sm focus:border-primary focus:bg-white focus:outline-none focus:ring-1 focus:ring-primary"
                            />
                            {search && (
                                <button
                                    type="button"
                                    onClick={() => setSearch('')}
                                    aria-label="Limpar busca"
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    <PiXBold />
                                </button>
                            )}
                        </div>
                        {q && (
                            <span className="text-xs tabular-nums text-gray-500">
                                {viewMode === 'sku'
                                    ? visibleRows.length
                                    : visibleOrderGroups.reduce((acc, g) => acc + g.rows.length, 0)}{' '}
                                resultado(s)
                            </span>
                        )}
                        <div className="ml-auto">
                            <ColorLegend prefix="Cores:" />
                        </div>
                    </div>

                    {viewMode === 'order' ? (
                        <p className="text-xs text-gray-600">
                            Visão por pedido: cada bloco é um pedido Ploomes/PI, como na tela da PO.
                            Clique no cabeçalho para expandir ou recolher.
                        </p>
                    ) : null}

                    {viewMode === 'sku' ? (
                        <ComparisonTable
                            rows={visibleRows}
                            hideOrderColumn={false}
                            motionKey={motionKey}
                            emptyMessage={q ? 'Nenhum item encontrado com essa busca.' : emptyFilterMessage(filter)}
                            onEditSku={setSkuModalRow}
                            onEditColor={(row, slot) => setColorModal({ row, slot })}
                        />
                    ) : visibleOrderGroups.length > 0 ? (
                        <OrderBlocks motionKey={motionKey}>
                            {visibleOrderGroups.map((group) => (
                                <OrderComparisonBlock
                                    key={group.order_number}
                                    group={group}
                                    collapsed={collapsedOrders.has(group.order_number)}
                                    onToggle={() => toggleOrderCollapsed(group.order_number)}
                                    onEditSku={setSkuModalRow}
                                    onEditColor={(row, slot) => setColorModal({ row, slot })}
                                />
                            ))}
                        </OrderBlocks>
                    ) : (
                        <div className="rounded-xl border border-gray-200 bg-white py-10 text-center text-sm text-gray-500 shadow-sm">
                            {q ? 'Nenhum item encontrado com essa busca.' : emptyFilterMessage(filter)}
                        </div>
                    )}
                </>
            )}

            <PiCheckColorModal
                open={colorModal != null}
                slot={colorModal?.slot ?? null}
                slotDetail={
                    colorModal
                        ? (colorModal.row.color_slots?.[colorModal.slot] ?? {
                              po_raw: colorModal.row.po?.[colorModal.slot] ?? null,
                              pi_raw: colorModal.row.pi?.[colorModal.slot] ?? null,
                              resolved_po: null,
                              resolved_pi: null,
                          })
                        : null
                }
                productName={colorModal?.row.product_name}
                onClose={() => setColorModal(null)}
                onSaved={() => {
                    void qc.invalidateQueries({ queryKey });
                    setToast({ kind: 'success', msg: 'Vínculo de cor salvo.' });
                }}
            />

            <ProductZoomModal row={zoomRow} onClose={() => setZoomRow(null)} />

            <PiCheckSkuModal
                row={skuModalRow}
                open={skuModalRow != null}
                onClose={() => setSkuModalRow(null)}
                onSaved={() => {
                    qc.invalidateQueries({ queryKey });
                    setToast({ kind: 'success', msg: 'SKUs atualizados. Conferência recalculada.' });
                }}
            />
        </div>
        </ZoomRowContext.Provider>
        </ColorHexMapsContext.Provider>
    );
}

function AnimatedNumber({ value, format }: { value: number; format: (n: number) => string }) {
    return <>{format(useCountUp(value))}</>;
}

function ConferenceProgress({ matched, total }: { matched: number; total: number }) {
    const barRef = useRef<HTMLDivElement>(null);
    const percent = total > 0 ? (matched / total) * 100 : 0;
    const shown = useCountUp(percent);
    const done = total > 0 && matched === total;
    const doneRef = useRef<HTMLSpanElement>(null);
    useFillBar(barRef, percent);

    useEffect(() => {
        if (done) pop(doneRef.current);
    }, [done]);

    return (
        <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
            <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
                <span className="text-sm font-semibold text-gray-800">
                    {done ? (
                        <span ref={doneRef} className="inline-flex items-center gap-1 text-emerald-700">
                            <PiCheckCircleBold /> PI conferida: todos os SKUs batem com a PO
                        </span>
                    ) : (
                        <>
                            {matched} de {total} SKUs conferidos
                        </>
                    )}
                </span>
                <span className={`text-sm font-bold tabular-nums ${done ? 'text-emerald-600' : 'text-gray-700'}`}>
                    {Math.round(shown)}%
                </span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-gray-100">
                <div
                    ref={barRef}
                    className={`h-full rounded-full ${done ? 'bg-emerald-500' : 'bg-gradient-to-r from-amber-400 to-emerald-500'}`}
                    style={{ width: 0 }}
                />
            </div>
        </div>
    );
}

function OrderBlocks({ motionKey, children }: { motionKey: string; children: ReactNode }) {
    const ref = useRef<HTMLDivElement>(null);
    useStaggerIn(ref, motionKey);
    return (
        <div ref={ref} className="space-y-4">
            {children}
        </div>
    );
}

function ProductZoomModal({ row, onClose }: { row: ComparisonRow | null; onClose: () => void }) {
    const hexMaps = useColorHexMaps();
    const listRef = useRef<HTMLUListElement>(null);
    useStaggerIn(listRef, row ? `${row.product_code}-${row.order_number ?? ''}` : '', row !== null);
    const imageUrl = row?.po?.image_url ?? row?.pi?.image_url ?? null;
    const name = row?.po?.name ?? row?.pi?.name ?? row?.product_name ?? '';
    const diff = row?.color_diff ?? [];

    return (
        <V2Modal
            open={row !== null}
            onClose={onClose}
            title={name}
            description={row ? (row.internal_code || row.product_code || undefined) : undefined}
            size="5xl"
        >
            {row && (
                <div className="grid gap-5 md:grid-cols-[minmax(0,1fr)_19rem]">
                    <div className="flex items-center justify-center rounded-xl bg-gray-50 p-4">
                        {imageUrl ? (
                            <img
                                src={imageUrl}
                                alt={name}
                                onLoad={(e) => fadeZoomIn(e.currentTarget)}
                                className="max-h-[60vh] w-auto object-contain"
                            />
                        ) : (
                            <PiCubeBold className="size-16 text-gray-300" />
                        )}
                    </div>
                    <div>
                        <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Cores PO × PI</h3>
                        <ul ref={listRef} className="space-y-2">
                            {COLOR_SLOTS.map((slot) => {
                                const poName = row.po?.[slot] ?? null;
                                const piName = row.pi?.[slot] ?? null;
                                const mismatch = diff.includes(slot);
                                return (
                                    <li
                                        key={slot}
                                        data-motion-item
                                        className={`rounded-xl border p-3 shadow-sm ${
                                            mismatch ? 'border-amber-300 bg-amber-50' : 'border-gray-100 bg-white'
                                        }`}
                                    >
                                        <div className="mb-2 flex items-center justify-between">
                                            <span className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
                                                {COLOR_SLOT_LABELS[slot]}
                                            </span>
                                            {mismatch ? (
                                                <span className="rounded-full bg-amber-200 px-2 py-0.5 text-[10px] font-bold text-amber-900">
                                                    ≠ diverge
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-emerald-600">
                                                    <PiCheckBold /> bate
                                                </span>
                                            )}
                                        </div>
                                        {(['PO', 'PI'] as const).map((side) => {
                                            const value = side === 'PO' ? poName : piName;
                                            return (
                                                <div key={side} className="flex items-center gap-2 py-0.5">
                                                    <span
                                                        className={`w-6 text-[10px] font-bold ${side === 'PO' ? 'text-emerald-700' : 'text-blue-700'}`}
                                                    >
                                                        {side}
                                                    </span>
                                                    <ColorDot slot={slot} name={value} hexMaps={hexMaps} size="md" />
                                                    <span className="truncate text-sm text-gray-800">
                                                        {isNoCustomization(value) ? (
                                                            <span className="text-gray-400">Sem customização</span>
                                                        ) : (
                                                            value
                                                        )}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                </div>
            )}
        </V2Modal>
    );
}

function ContainersStrip({
    totalCbm,
    containersCount,
    cbmPerContainer,
    accent,
    diffLabel,
}: {
    totalCbm: number;
    containersCount: number;
    cbmPerContainer: number;
    accent: 'emerald' | 'blue';
    diffLabel?: string;
}) {
    const fullUsed = Math.floor(totalCbm / cbmPerContainer);
    const hasPartial = totalCbm - fullUsed * cbmPerContainer > 0.0001;
    const slots = Math.max(containersCount, 1);
    const cols = slots <= 5 ? 5 : slots <= 10 ? 5 : 6;

    const gridRef = useRef<HTMLDivElement>(null);
    useStaggerIn(gridRef, `containers-${accent}`);

    return (
        <div className="mt-4 border-t border-gray-200/80 pt-4">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                    Containers ({cbmPerContainer} m³)
                </span>
                <span
                    className={`text-sm font-bold tabular-nums ${
                        accent === 'emerald' ? 'text-emerald-700' : 'text-blue-700'
                    }`}
                >
                    {fmtContainers(containersCount)}
                </span>
            </div>
            <div ref={gridRef} className="grid gap-2" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
                {Array.from({ length: slots }).map((_, i) => {
                    const n = i + 1;
                    const isFull = n <= fullUsed;
                    const isPartial = !isFull && n === fullUsed + 1 && hasPartial;
                    const slotCbm = Math.min(cbmPerContainer, Math.max(0, totalCbm - i * cbmPerContainer));
                    return (
                        <ContainerSlot
                            key={i}
                            n={n}
                            slotCbm={slotCbm}
                            cbmPer={cbmPerContainer}
                            tone={isFull ? (accent === 'emerald' ? 'full' : 'fullBlue') : isPartial ? 'partial' : 'empty'}
                        />
                    );
                })}
            </div>
            {diffLabel ? (
                <p
                    className={`mt-2 text-center text-xs font-medium ${
                        diffLabel.startsWith('Δ')
                            ? 'text-amber-700'
                            : 'text-emerald-700'
                    }`}
                >
                    {diffLabel}
                </p>
            ) : null}
        </div>
    );
}

function SummaryCard({
    title,
    subtitle,
    items,
    accent,
    footer,
}: {
    title: string;
    subtitle: string;
    items: { label: string; value: ReactNode }[];
    accent?: 'emerald';
    footer?: ReactNode;
}) {
    const accentRing =
        accent === 'emerald' ? 'ring-emerald-200 bg-emerald-50/40' : 'ring-gray-200 bg-white';
    return (
        <div className={`rounded-xl p-5 shadow-sm ring-1 ${accentRing}`}>
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-gray-500">
                {title}
            </div>
            <div className="mt-1 text-xl font-bold text-gray-800">{subtitle}</div>
            <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                {items.map((i) => (
                    <div key={i.label}>
                        <div className="text-[10px] uppercase tracking-wider text-gray-500">
                            {i.label}
                        </div>
                        <div className="font-medium tabular-nums text-gray-800">{i.value}</div>
                    </div>
                ))}
            </div>
            {footer}
        </div>
    );
}

function FilterChip({
    color,
    label,
    value,
    icon,
    active,
    onClick,
    disabled = false,
}: {
    color: 'primary' | 'emerald' | 'red' | 'amber' | 'rose' | 'slate';
    label: string;
    value: number;
    icon?: React.ReactNode;
    active: boolean;
    onClick: () => void;
    disabled?: boolean;
}) {
    const map: Record<typeof color, string> = {
        primary: 'bg-gray-50 text-gray-800 ring-gray-200 hover:bg-gray-100',
        emerald: 'bg-emerald-50 text-emerald-800 ring-emerald-200 hover:bg-emerald-100',
        red: 'bg-red-50 text-red-800 ring-red-200 hover:bg-red-100',
        amber: 'bg-amber-50 text-amber-800 ring-amber-200 hover:bg-amber-100',
        rose: 'bg-rose-50 text-rose-800 ring-rose-200 hover:bg-rose-100',
        slate: 'bg-slate-50 text-slate-700 ring-slate-200 hover:bg-slate-100',
    };
    const activeRing =
        color === 'primary'
            ? 'ring-2 ring-primary ring-offset-1'
            : 'ring-2 ring-offset-1 ring-current';

    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-left ring-1 transition duration-200 hover:-translate-y-0.5 hover:shadow-sm ${map[color]} ${
                active ? activeRing : ''
            } ${disabled ? 'cursor-not-allowed opacity-40' : 'cursor-pointer'}`}
        >
            <div className="flex items-center gap-1.5 text-xs font-medium">
                {icon}
                <span>{label}</span>
            </div>
            <span className="text-lg font-bold tabular-nums">
                <AnimatedNumber value={value} format={(n) => String(Math.round(n))} />
            </span>
        </button>
    );
}


function SkuCell({ r, onEdit }: { r: ComparisonRow; onEdit: () => void }) {
    const internal = r.internal_code || r.product_code || '—';
    const supplier = r.supplier_code || '—';

    return (
        <td className="w-36 max-w-36 px-2 py-1.5 align-middle font-mono text-[11px] bg-slate-50/80">
            <div className="font-semibold text-gray-900 leading-tight">{internal}</div>
            {supplier !== '—' && supplier !== internal ? (
                <div className="truncate text-[10px] text-gray-500" title={`Fornecedor: ${supplier}`}>
                    {supplier}
                </div>
            ) : null}
            <button
                type="button"
                onClick={onEdit}
                className="mt-0.5 inline-flex items-center gap-0.5 text-[10px] font-medium text-primary hover:underline"
            >
                <PiPencilSimpleBold className="size-2.5" />
                SKUs
            </button>
            {!r.sku_linked ? (
                <div className="text-[9px] font-medium text-amber-700">Sem catálogo</div>
            ) : null}
        </td>
    );
}

function ViewModeToggle({
    mode,
    onChange,
}: {
    mode: ViewMode;
    onChange: (m: ViewMode) => void;
}) {
    return (
        <div
            className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5 text-xs font-medium"
            role="group"
            aria-label="Modo de visualização"
        >
            <button
                type="button"
                onClick={() => onChange('sku')}
                className={
                    'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 transition-colors ' +
                    (mode === 'sku'
                        ? 'bg-white text-gray-900 shadow-sm ring-1 ring-gray-200'
                        : 'text-gray-600 hover:text-gray-900')
                }
            >
                <PiSquaresFourBold className="size-4" />
                Por cor
            </button>
            <button
                type="button"
                onClick={() => onChange('order')}
                className={
                    'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 transition-colors ' +
                    (mode === 'order'
                        ? 'bg-white text-gray-900 shadow-sm ring-1 ring-gray-200'
                        : 'text-gray-600 hover:text-gray-900')
                }
            >
                <PiListBulletsBold className="size-4" />
                Por pedido
            </button>
        </div>
    );
}

const COLOR_COLUMNS: {
    key: 'frame' | 'upholstery' | 'stitches' | 'arm';
    label: string;
    short: string;
}[] = [
    { key: 'frame', label: 'Estrutura', short: 'Estr.' },
    { key: 'upholstery', label: 'Estofado', short: 'Est.' },
    { key: 'stitches', label: 'Costura', short: 'Cost.' },
    { key: 'arm', label: 'Braço', short: 'Braço' },
];

function ComparisonTableHead({ hideOrderColumn }: { hideOrderColumn: boolean }) {
    return (
        <thead className="sticky top-0 z-10 bg-gray-50 text-[10px] font-semibold uppercase tracking-wide text-gray-600 shadow-sm">
            <tr>
                <th rowSpan={2} className="w-16 px-2 py-1.5 text-left align-bottom">
                    Status
                </th>
                <th rowSpan={2} className="w-32 bg-slate-50/80 px-2 py-1.5 text-left align-bottom">
                    SKU
                </th>
                {!hideOrderColumn ? (
                    <th rowSpan={2} className="min-w-[5.5rem] px-2 py-1.5 text-left align-bottom">
                        Pedido
                    </th>
                ) : null}
                <th rowSpan={2} className="min-w-[180px] px-2 py-1.5 text-left align-bottom">
                    Equipamento
                </th>
                <th
                    colSpan={4}
                    className="border-l border-violet-200 bg-violet-50/80 px-2 py-1 text-center text-violet-900"
                >
                    Cores (PO · PI)
                </th>
                <th
                    colSpan={3}
                    className="border-l-2 border-emerald-300 bg-emerald-50/90 px-2 py-1 text-center text-emerald-900"
                >
                    Quantidade
                </th>
            </tr>
            <tr className="normal-case text-[9px] font-medium text-gray-500">
                {COLOR_COLUMNS.map(({ key, label }) => (
                    <th
                        key={key}
                        className="w-24 border-l border-violet-100 bg-violet-50/50 px-1 py-1 text-center text-violet-800"
                    >
                        {label}
                    </th>
                ))}
                <th className="w-14 border-l-2 border-emerald-200 bg-emerald-50/80 px-1 py-1 text-right text-emerald-800">
                    PO
                </th>
                <th className="w-14 bg-blue-50/80 px-1 py-1 text-right text-blue-800">PI</th>
                <th className="w-14 bg-amber-50/60 px-1 py-1 text-right text-amber-800">Δ</th>
            </tr>
        </thead>
    );
}

function comparisonTableColSpan(hideOrderColumn: boolean): number {
    return (hideOrderColumn ? 3 : 4) + COLOR_COLUMNS.length + 3;
}

function ComparisonTable({
    rows,
    hideOrderColumn,
    emptyMessage,
    onEditSku,
    onEditColor,
    embedded = false,
    motionKey,
}: {
    rows: ComparisonRow[];
    hideOrderColumn: boolean;
    emptyMessage: string;
    onEditSku: (r: ComparisonRow) => void;
    onEditColor: (r: ComparisonRow, slot: ColorSlotKey) => void;
    embedded?: boolean;
    motionKey?: string;
}) {
    const colSpan = comparisonTableColSpan(hideOrderColumn);
    const bodyRef = useRef<HTMLTableSectionElement>(null);
    useStaggerIn(bodyRef, motionKey ?? '', motionKey !== undefined);

    const table = (
            <table className="min-w-full text-xs">
                <ComparisonTableHead hideOrderColumn={hideOrderColumn} />
                <tbody ref={bodyRef} className="divide-y divide-gray-100">
                    {rows.map((r, idx) => (
                        <Row
                            animate={idx < MOTION_ROWS}
                            key={`${r.order_number ?? ''}-${r.product_code}-${r.po_source_codes.join('-')}-${r.pi_source_codes.join('-')}`}
                            r={r}
                            hideOrderColumn={hideOrderColumn}
                            onEditSku={() => onEditSku(r)}
                            onEditColor={(slot) => onEditColor(r, slot)}
                        />
                    ))}
                    {rows.length === 0 && (
                        <tr>
                            <td colSpan={colSpan} className="py-10 text-center text-sm text-gray-500">
                                {emptyMessage}
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
    );

    if (embedded) {
        return <div className="overflow-x-auto">{table}</div>;
    }

    return (
        <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
            {table}
        </div>
    );
}

function OrderComparisonBlock({
    group,
    collapsed,
    onToggle,
    onEditSku,
    onEditColor,
}: {
    group: OrderGroup;
    collapsed: boolean;
    onToggle: () => void;
    onEditSku: (r: ComparisonRow) => void;
    onEditColor: (r: ComparisonRow, slot: ColorSlotKey) => void;
}) {
    const issues = group.rows.filter((r) => r.status !== 'ok').length;
    const clientName =
        group.rows.find((r) => r.po?.client_name)?.po?.client_name ??
        group.rows.find((r) => r.pi?.client_name)?.pi?.client_name ??
        null;

    return (
        <div data-motion-item className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            <button
                type="button"
                onClick={onToggle}
                aria-expanded={!collapsed}
                className={`flex w-full flex-wrap items-center justify-between gap-2 bg-gray-50 px-4 py-3 text-left transition-colors hover:bg-gray-100 ${
                    collapsed ? '' : 'border-b border-gray-200'
                }`}
            >
                <div className="flex items-center gap-3">
                    <PiCaretDownBold
                        className={`size-4 text-gray-500 transition-transform duration-300 ${collapsed ? '-rotate-90' : ''}`}
                    />
                    <div className="flex size-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        <PiFileTextBold />
                    </div>
                    <div>
                        <div className="font-semibold text-gray-800">
                            Pedido {group.order_number}
                        </div>
                        <div className="text-xs text-gray-500">
                            {group.rows.length} produto(s)
                            {clientName ? ` · ${clientName}` : ''}
                        </div>
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-2 text-xs">
                    {group.summary.matched > 0 ? (
                        <span className="rounded-full bg-emerald-100 px-2 py-0.5 font-medium text-emerald-800">
                            {group.summary.matched} OK
                        </span>
                    ) : null}
                    {issues > 0 ? (
                        <span className="rounded-full bg-yellow-200 px-2 py-0.5 font-medium text-yellow-900">
                            {issues} divergência(s)
                        </span>
                    ) : null}
                </div>
            </button>
            {!collapsed ? (
                <ComparisonTable
                    rows={group.rows}
                    hideOrderColumn
                    embedded
                    emptyMessage="Nenhum item neste pedido."
                    onEditSku={onEditSku}
                    onEditColor={onEditColor}
                />
            ) : null}
        </div>
    );
}

function ColorColumnCell({
    slotKey,
    po,
    pi,
    mismatch,
    slotDetail,
    onEdit,
}: {
    slotKey: ColorSlotKey;
    po: Side | null;
    pi: Side | null;
    mismatch: boolean;
    slotDetail?: ColorSlotDetail;
    onEdit: () => void;
}) {
    const poVal = po?.[slotKey]?.trim() || '';
    const piVal = pi?.[slotKey]?.trim() || '';
    const empty = !poVal && !piVal;
    const colorHexMaps = useColorHexMaps();
    const match = !empty && !mismatch;
    const aliasMapped =
        slotDetail?.alias_matched_po || slotDetail?.alias_matched_pi;
    const cellTitle = aliasMapped
        ? 'Cor mapeada no catálogo. Clique para editar o vínculo.'
        : 'Clique para vincular cor ao catálogo';

    return (
        <td
            role="button"
            tabIndex={0}
            onClick={onEdit}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onEdit();
                }
            }}
            title={cellTitle}
            className={`w-24 cursor-pointer border-l border-violet-100 px-1.5 py-1.5 align-top transition-colors hover:bg-violet-100/60 ${
                mismatch
                    ? 'bg-amber-100 ring-1 ring-inset ring-amber-400'
                    : match
                      ? 'bg-emerald-50/50'
                      : 'bg-violet-50/20'
            }`}
        >
            {empty ? (
                <span className="text-gray-300">—</span>
            ) : (
                <div className="flex flex-col items-center gap-1 text-center">
                    <ColorDot slot={slotKey} name={poVal || piVal || null} hexMaps={colorHexMaps} size="md" />
                    <div className="w-full space-y-0.5 leading-tight">
                        <div
                            className="truncate rounded bg-emerald-100/90 px-1 text-[9px] font-medium text-emerald-900"
                            title={`PO: ${poVal || '—'}`}
                        >
                            {poVal || '—'}
                        </div>
                        <div
                            className="truncate rounded bg-blue-100/90 px-1 text-[9px] font-medium text-blue-900"
                            title={`PI: ${piVal || '—'}`}
                        >
                            {piVal || '—'}
                        </div>
                    </div>
                    {mismatch ? (
                        <span className="text-[8px] font-bold uppercase text-amber-800">≠</span>
                    ) : (
                        <span className="text-[8px] font-bold text-emerald-600">✓</span>
                    )}
                </div>
            )}
        </td>
    );
}

function ProductCell({
    r,
}: {
    r: ComparisonRow;
}) {
    const imageUrl = r.po?.image_url ?? r.pi?.image_url ?? null;
    const productName = r.po?.name ?? r.pi?.name ?? '—';
    const openZoom = useContext(ZoomRowContext);
    const clients =
        r.clients?.length
            ? r.clients
            : (() => {
                  const name = r.po?.client_name ?? r.pi?.client_name;
                  if (!name) return [];
                  return [
                      {
                          id: r.po?.client_id ?? r.pi?.client_id,
                          name,
                          logo_url: r.po?.client_logo_url ?? r.pi?.client_logo_url,
                          abbreviation: name.slice(0, 2).toUpperCase(),
                      },
                  ] as ClientThumb[];
              })();

    return (
        <div className="flex min-w-0 items-start gap-2">
            {imageUrl ? (
                <button
                    type="button"
                    onClick={() => openZoom(r)}
                    title="Ampliar imagem e comparar cores"
                    aria-label={`Ampliar imagem de ${productName}`}
                    className="group shrink-0 cursor-zoom-in rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                >
                    <img
                        src={imageUrl}
                        alt=""
                        loading="lazy"
                        className="size-9 rounded-md object-cover ring-1 ring-gray-200 transition duration-200 group-hover:scale-110 group-hover:ring-primary/50"
                    />
                </button>
            ) : (
                <div className="flex size-9 shrink-0 items-center justify-center rounded-md bg-gray-100 ring-1 ring-gray-200">
                    <PiCubeBold className="size-4 text-gray-400" />
                </div>
            )}
            <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-gray-900" title={productName}>
                    {productName}
                </p>
                {clients.length > 0 ? (
                    <div className="mt-1">
                        <ClientAvatarStack clients={clients} />
                    </div>
                ) : null}
            </div>
        </div>
    );
}

function mismatchBadgeLabel(issues: string[]): string {
    const hasQty = issues.includes('quantity');
    const hasColor = issues.includes('color');
    if (hasQty && hasColor) return 'QTD+COR';
    if (hasQty) return 'QTD';
    if (hasColor) return 'COR';

    return 'DIVERGÊNCIA';
}

function QtyCell({ side }: { side: Side | null }) {
    if (!side) {
        return <span className="text-gray-400">—</span>;
    }
    const lines = side.line_count ?? 1;
    const title = lines > 1 ? `${lines} linhas somadas neste SKU` : undefined;

    return (
        <span className="text-sm font-semibold tabular-nums" title={title}>
            {fmtQty(side.qty)}
        </span>
    );
}

function Row({
    r,
    onEditSku,
    onEditColor,
    hideOrderColumn = false,
    animate = false,
}: {
    r: ComparisonRow;
    onEditSku: () => void;
    onEditColor: (slot: ColorSlotKey) => void;
    hideOrderColumn?: boolean;
    animate?: boolean;
}) {
    const colorDiff = r.color_diff ?? [];
    const qtyIssue = r.issues.includes('quantity');
    const qtyHighlight = qtyIssue ? 'bg-red-50 font-bold text-red-800' : '';

    const rowClass =
        r.status === 'ok'
            ? 'bg-white hover:bg-gray-50/80'
            : r.status === 'mismatch'
              ? 'bg-amber-50/40 hover:bg-amber-50/70'
              : r.status === 'only_in_pi'
                ? 'bg-rose-50/30'
                : 'bg-slate-50/50';

    const badge =
        r.status === 'ok' ? (
            <span className="inline-flex rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold text-emerald-800">
                OK
            </span>
        ) : r.status === 'mismatch' ? (
            <span className="inline-flex rounded-full bg-amber-200 px-1.5 py-0.5 text-[10px] font-bold text-amber-950">
                {mismatchBadgeLabel(r.issues)}
            </span>
        ) : r.status === 'only_in_pi' ? (
            <span className="inline-flex rounded-full bg-rose-200 px-1.5 py-0.5 text-[10px] font-bold text-rose-900">
                +PI
            </span>
        ) : (
            <span className="inline-flex rounded-full bg-slate-200 px-1.5 py-0.5 text-[10px] font-bold text-slate-700">
                +PO
            </span>
        );

    return (
        <tr data-motion-item={animate ? '' : undefined} className={`${rowClass} border-b border-gray-100 transition-colors`}>
            <td className="px-2 py-1.5 align-middle">{badge}</td>
            <SkuCell r={r} onEdit={onEditSku} />
            {!hideOrderColumn ? (
                <td className="px-2 py-1.5 align-middle">
                    <OrderRefAvatarStack refs={r.order_refs ?? []} />
                </td>
            ) : null}
            <td className="px-2 py-1.5 align-middle">
                <ProductCell r={r} />
            </td>
            {COLOR_COLUMNS.map(({ key }) => (
                <ColorColumnCell
                    key={key}
                    slotKey={key}
                    po={r.po}
                    pi={r.pi}
                    mismatch={colorDiff.includes(key)}
                    slotDetail={r.color_slots?.[key]}
                    onEdit={() => onEditColor(key)}
                />
            ))}
            <td className="border-l-2 border-emerald-200 bg-emerald-50/50 px-2 py-1.5 text-right align-middle">
                <QtyCell side={r.po} />
            </td>
            <td className="bg-blue-50/50 px-2 py-1.5 text-right align-middle">
                <QtyCell side={r.pi} />
            </td>
            <td
                className={`bg-amber-50/40 px-2 py-1.5 text-right align-middle tabular-nums ${qtyHighlight}`}
            >
                {r.qty_diff === null
                    ? '—'
                    : (r.qty_diff > 0 ? '+' : '') + fmtQty(r.qty_diff)}
            </td>
        </tr>
    );
}
